import { Grid } from '@mui/material';
import { useFormik } from 'formik';
import { useEffect } from 'react';
import { useAlmacenesStore } from '../../stores/almacenes/almacenes.store';
import type { Articulo } from '../../stores/articulos/articulo';
import type { ActualizarArticulo, RegistrarArticulo } from '../../stores/articulos/articulos.store';
import { useClasificacionesStore } from '../../stores/clasificaciones/clasificaciones.store';
import { useUIStore } from '../../stores/ui/ui.store';
import type { CommonEntityFormProps } from '../../utils/common-entity-form-props';
import { getLengthOrErrorText } from '../../utils/get-length-or-error-text';
import transformToOptions from '../../utils/transform-to-options';
import AppTextField from '../ui/AppTextField';
import ComboBox from '../ui/Combobox';
import EditForm from '../ui/EditForm';
import { validationSchema, type ArticuloFormValues } from './articulo.schema';
import ArticuloTabs from './ArticuloTabs';

type ArticuloFormProps = {
  articulo?: Articulo;
  onSave:
    | ((values: ActualizarArticulo) => Promise<void>)
    | ((values: RegistrarArticulo) => Promise<boolean>);
} & CommonEntityFormProps;

const ArticuloForm = ({ articulo, esAgregar, onSave, onDelete, onActivate }: ArticuloFormProps) => {
  const esEditar = !esAgregar;
  const activo = esAgregar || articulo?.activo === true;
  const inactivo = !activo;
  const status = activo ? 'ACTIVO' : 'INACTIVO';
  const { loading } = useUIStore((state) => state);
  const { actives: clasificaciones, cargarActivas: cargarClasificacionesActivas } =
    useClasificacionesStore((state) => state);
  const { actives: almacenes, cargarActivos: cargarAlmacenesActivos } = useAlmacenesStore();
  const initialValues: ArticuloFormValues = {
    id: articulo?.id ?? '',
    descripcion: articulo?.descripcion ?? '',
    idClasificacion: articulo?.idClasificacion ?? 0,
    agrupaciones: articulo?.agrupaciones ?? [],
    equivalencias: articulo?.equivalencias ?? [],
    inventario: articulo?.inventario ?? []
  };
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      const success = await onSave({
        id: values.id,
        descripcion: values.descripcion,
        idClasificacion: values.idClasificacion,
        idAgrupaciones: values.agrupaciones.map((x) => x.id),
        equivalencias: values.equivalencias,
        ubicacionesAlmacen: values.inventario
      });
      if (success) {
        formik.resetForm();
      }
    },
    enableReinitialize: true
  });

  const unidadBase = formik.values.equivalencias.find((e) => e.cantidad === 1);

  useEffect(() => {
    if (almacenes.length === 0) return;

    formik.setFieldValue(
      'inventario',
      almacenes.map((a) => ({
        idAlmacen: a.id,
        almacen: a.nombre,
        cantidad: 0,
        idUnidad: unidadBase?.idUnidad ?? '',
        idUbicacion: 0,
        ubicacion: ''
      }))
    );
  }, [almacenes]);

  useEffect(() => {
    formik.setFieldValue(
      'inventario',
      formik.values.inventario.map((a) => ({
        idAlmacen: a.idAlmacen,
        almacen: a.almacen,
        existencia: a.existencia,
        idUnidad: unidadBase?.idUnidad ?? '',
        idUbicacion: a.idUbicacion,
        ubicacion: a.ubicacion
      }))
    );
  }, [unidadBase]);

  useEffect(() => {
    cargarClasificacionesActivas();
    if (esAgregar) {
      cargarAlmacenesActivos();
    }
  }, []);

  const clasificacionesOptions = transformToOptions(clasificaciones);

  const reset = () => formik.resetForm({ values: initialValues });

  return (
    <EditForm
      module="articulos"
      title="Artículos"
      readonly={!activo}
      returnLink="/articulos"
      loading={loading}
      showActivate={false}
      showDelete={esEditar}
      onSave={formik.handleSubmit}
      onClear={reset}
      onDelete={onDelete}
      onActivate={onActivate}
    >
      <Grid>
        <Grid container spacing={2}>
          <Grid size={4}>
            <AppTextField
              name="id"
              label="Código"
              placeholder="Código"
              maxLength={20}
              uppercase={true}
              value={formik.values.id}
              readOnly={esEditar}
              disabled={loading}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.id && Boolean(formik.errors.id)}
              helperText={getLengthOrErrorText(
                formik.touched.id ? formik.errors.id : '',
                formik.values.id.length,
                20
              )}
            />
          </Grid>
          <Grid size="grow">
            <AppTextField
              name="descripcion"
              label="Descripción"
              placeholder="Descripción"
              maxLength={40}
              disabled={loading || inactivo}
              value={formik.values.descripcion}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.descripcion && Boolean(formik.errors.descripcion)}
              helperText={getLengthOrErrorText(
                formik.touched.descripcion ? formik.errors.descripcion : '',
                formik.values.descripcion.length,
                40
              )}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid size="grow">
            <ComboBox
              label="Clasificación"
              name="idClasificacion"
              value={formik.values.idClasificacion}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              options={clasificacionesOptions}
              error={formik.touched.idClasificacion && Boolean(formik.errors.idClasificacion)}
              helperText={formik.touched.idClasificacion ? formik.errors.idClasificacion : ''}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid>
        <Grid container>
          <AppTextField label="Status" value={status} disabled={true} />
        </Grid>
      </Grid>
      <Grid>
        <ArticuloTabs formik={formik} inactivo={inactivo} loading={loading} />
      </Grid>
    </EditForm>
  );
};

export default ArticuloForm;
